import {useQuery} from '@tanstack/react-query'

import {DM_SERVICE_HEADERS} from '#/lib/constants'
import {STALE} from '#/state/queries'
import {useAgent, useSession} from '#/state/session'
import {useAgeAssurance} from '#/ageAssurance'

const RQKEY_ROOT = 'convo-unread-counts'
export const RQKEY = (includeGroupChats: boolean) =>
  [RQKEY_ROOT, includeGroupChats] as const
export const RQKEY_PARTIAL = [RQKEY_ROOT] as const

// The server sentinel-caps the badge counts: unreadAcceptedConvos and
// unreadRequestConvos max out at 100 (meaning "more than 99"). At the cap the
// value is no longer an exact count, so consumers must not treat it as one -
// both the optimistic decrement and the badge display ceiling key off these.
export const UNREAD_ACCEPTED_CAP = 100
export const UNREAD_REQUEST_CAP = 100

type UnreadCountsResponse = {
  unreadAcceptedConvos: number
  unreadRequestConvos: number
  [k: string]: unknown
}

export function useUnreadCountsQuery() {
  const agent = useAgent()
  const {hasSession} = useSession()
  const aa = useAgeAssurance()
  // groupChatDisabled is not yet available; fall back to chatDisabled as a
  // conservative guard until the age-assurance flag is wired in a later task.
  const includeGroupChats = !aa.flags.chatDisabled

  return useQuery({
    queryKey: RQKEY(includeGroupChats),
    queryFn: async () => {
      const res = await agent.chat._client.call(
        'chat.bsky.convo.getConvoUnreadCount',
        {includeGroupChats},
        undefined,
        {headers: DM_SERVICE_HEADERS},
      )
      // TODO: type properly when @atproto/api exposes chat.bsky.convo.getConvoUnreadCount schema
      return res.data as UnreadCountsResponse
    },
    staleTime: STALE.SECONDS.FIFTEEN,
    enabled: hasSession,
  })
}
